import { z } from 'zod/v4';

import { getExecutionColumnType, StudioExecutionColumnTypes } from '@blockworks/platform/api/research/studio';

import { ExecutionMetadata } from './core';
import { RowIndexFieldSchema } from './fields';

// Create a branded column reference type for specific columns
export type ColumnReference<T extends string = string> = T & z.BRAND<'ColumnReference'>;

// Transform to validate columns at parse time with generic type support
export const createColumnTransform = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    return z
        .string()
        .transform(col => {
            return col as ColumnReference<TColumns>;
        })
        .refine(val => val in meta.columnTypes, {
            error: issue => `Column not found: ${issue.input} `,
        });
};

// Transform to validate row indices at parse time
export const createRowTransform = <TColumns extends string = string>(meta: ExecutionMetadata<TColumns>) => {
    const rowCount = meta.rowCount ?? 0;
    return z
        .number()
        .transform(rowIndex => {
            return rowIndex as z.infer<typeof RowIndexFieldSchema>;
        })
        .refine(rowIndex => !rowCount || rowIndex <= 0 || rowIndex >= rowCount - 1, {
            error: issue => `Row index out of bounds [0, ${rowCount - 1}]: ${issue.input}`,
        });
};

// Create typed column transform for specific column types
export const createTypedColumnTransform = <TColumns extends string = string>(
    meta: ExecutionMetadata<TColumns>,
    columnType: 'number' | 'string' | 'date' | 'boolean',
) => {
    // Handle both Map and Record types for columnTypes
    const entries =
        meta.columnTypes instanceof Map
            ? Array.from(meta.columnTypes.entries())
            : (Object.entries(meta.columnTypes) as [TColumns, StudioExecutionColumnTypes][]);

    const typedColumns = entries
        .filter(([_, colType]) => {
            const mappedType = getExecutionColumnType(colType as StudioExecutionColumnTypes);
            return mappedType === columnType;
        })
        .map(([col]) => col);

    return z
        .string()
        .transform(col => {
            return col as ColumnReference<TColumns>;
        })
        .refine(col => typedColumns.some(c => c === col), {
            error: issue => {
                if (typeof issue.input === 'string' && issue.input in meta.columnTypes) {
                    return `Type not numeric: ${issue.input}`;
                }
                return `Column not found: ${issue.input}`;
            },
        });
};
